import type {
	BoardFragmentFragment,
	TeamFragmentFragment,
} from '@apps/www/src/__generated__/graphql';
import useTeamHomePath from '@apps/www/src/www/hooks/useTeamHomePath';
import SVLink from '@pkgs/shared-client/components/SVLink';
import SVTopNavSpacer from '@pkgs/shared-client/components/SVTopNavSpacer';
import cn from '@pkgs/shared-client/helpers/cn';
import matchLocation from '@pkgs/shared-client/helpers/matchLocation';
import plural from '@pkgs/shared-client/helpers/plural';
import IconPlus2SVG from '@pkgs/shared-client/img/icon-plus2-inlined.svg';
import SidebarOpenState from '@pkgs/shared/enums/SidebarOpenState';
import { useRouter } from 'next/router';
import React, { useRef } from 'react';

const TYPES = {
	USER: 'user',
	TEAM: 'team',
} as const;

/**
 * Gets the appropriate board link based on type and current path
 */
const getBoardLink = (
	type: ValueOf<typeof TYPES>,
	pathname: string,
	board: BoardFragmentFragment,
	teamHomePath: string,
	team?: TeamFragmentFragment,
): string => {
	if (type === TYPES.USER) {
		return board.url;
	}

	// If the board is the default team feed, redirect to the team feed page
	if (team && board._id === team.feedBoardID) {
		return teamHomePath;
	}

	// For team boards, remove "/boards" from URL when not already on a boards page
	if (!pathname.includes('/boards')) {
		return board.url.replace('/boards', '');
	}

	return board.url;
};

type Props = {
	type: ValueOf<typeof TYPES>;
	openState: ValueOf<typeof SidebarOpenState>;
	boards: BoardFragmentFragment[];
	isOwner: boolean;
	onNewBoardClick: () => void;
	onToggleSidebar?: () => void;
	children: React.ReactNode;
	lastThumbnails?: string[];
	team?: TeamFragmentFragment;
};

const _BoardItem = React.memo(
	({
		type,
		openState,
		board,
		team,
		teamHomePath,
	}: Pick<Props, 'type' | 'openState'> & {
		board: BoardFragmentFragment;
		team?: TeamFragmentFragment;
		teamHomePath: string;
	}) => {
		const router = useRouter();
		const linkTo = getBoardLink(type, router.pathname, board, teamHomePath, team);
		const isActive = matchLocation(router.asPath, linkTo);

		return (
			<li className="-lg:border-gray-800 -lg:border -lg:rounded-[6px] -lg:min-w-[200px] block">
				<SVLink to={linkTo} className={cn('group relative', isActive && 'disabled')}>
					<span
						className={cn(
							'absolute block h-full w-full rounded-[6px] bg-gray-900 opacity-0 group-hover:opacity-100',
							isActive && 'opacity-100',
						)}
					/>
					<span
						className={cn(
							'duration-slide absolute block h-full w-full rounded-[10px] border-[1.5px] bg-transparent opacity-0 transition-all ease-out',
							isActive && 'opacity-100',
							isActive && openState === SidebarOpenState.MINIMAL
								? 'border-gray-400'
								: 'border-gray-900',
						)}
					/>
					<span className="relative flex h-full items-center gap-3 px-[6px] py-[3px]">
						<span
							className="block h-10 w-10 flex-shrink-0 rounded bg-gray-800 bg-cover"
							style={
								board.thumbnails.length > 0
									? {
											backgroundImage: `url(${board.thumbnails[0].image.thumbnail})`,
									  }
									: {}
							}
						/>
						<span className="flex min-w-0 flex-grow flex-col">
							<span className={cn('type-small truncate', isActive && 'text-primary')}>
								{board.name}
							</span>
							<span className="type-small truncate text-gray-500">
								{plural(board.itemsCount, 'Save')}
							</span>
						</span>
					</span>
				</SVLink>
			</li>
		);
	},
);

const NewBoardItem = React.memo(
	({ onClick, type }: { onClick: () => void; type: ValueOf<typeof TYPES> }) => {
		return (
			<li className="-lg:border-gray-800 -lg:border -lg:rounded-[6px] -lg:min-w-[200px] block min-h-full items-center justify-center">
				<button
					onClick={onClick}
					className="group relative block h-full w-full items-center justify-center"
				>
					<span className="absolute block h-full w-full rounded-[6px] bg-gray-900 opacity-0 group-hover:opacity-100" />
					<span className="relative flex h-full items-center gap-3 p-[6px]">
						<span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded bg-gray-900">
							<IconPlus2SVG className="h-3 w-3 text-gray-400" />
						</span>
						<span className="flex min-h-full min-w-0 flex-grow flex-col items-start justify-center">
							<span className="type-small truncate">
								New {type === TYPES.TEAM ? 'team' : ''} board
							</span>
						</span>
					</span>
				</button>
			</li>
		);
	},
);

const _ToggleButton = ({
	openState,
	onToggle,
}: Pick<Props, 'openState'> & {
	onToggle?: () => void;
}) => {
	const label =
		openState === SidebarOpenState.OPEN
			? 'Minimize side menu'
			: openState === SidebarOpenState.MINIMAL
			? 'Hide side menu'
			: 'Show side menu';

	return (
		<div className="-lg:hidden text-secondary z-30 mb-2 inline-flex h-12 w-[40px] items-center justify-center pt-[6px]">
			<button
				onClick={onToggle}
				aria-label={label}
				title={label}
				className="relative flex h-10 w-10 items-center justify-center rounded-full text-gray-400 transition-colors duration-200 hover:bg-gray-50 hover:bg-opacity-10"
			>
				<div className="relative flex h-5 w-5 flex-col items-center justify-center">
					<span
						className={cn(
							'absolute block h-[1.5px] w-4 rounded-full bg-current transition-transform duration-300',
							// isVisible ? 'rotate-45 transform' : 'translate-y-[-4px]',
							'translate-y-[-4px]',
						)}
					/>
					<span
						className={cn(
							'absolute block h-[1.5px] w-4 rounded-full bg-current transition-transform duration-300',
							// isVisible ? '-rotate-45 transform' : 'translate-y-[4px]',
							'translate-y-[4px]',
						)}
					/>
				</div>
			</button>
		</div>
	);
};

const _AllSavedItems = ({
	thumbnails,
	openState,
	teamHomePath,
}: Pick<Props, 'openState'> & {
	thumbnails: string[];
	teamHomePath: string;
}) => {
	const router = useRouter();
	const linkTo = teamHomePath;
	const isActive = matchLocation(router.asPath, linkTo);

	return (
		<li className="-lg:border-gray-800 -lg:border -lg:rounded-[6px] -lg:min-w-[200px] block min-h-full">
			<SVLink to={linkTo} className={cn('group relative h-full', isActive && 'disabled')}>
				<span
					className={cn(
						'absolute block h-full w-full rounded-[6px] bg-gray-900 opacity-0 group-hover:opacity-100',
						isActive && 'opacity-100',
					)}
				/>
				<span
					className={cn(
						'duration-slide relative flex h-full items-center gap-3 p-[6px]',
						isActive && openState === SidebarOpenState.MINIMAL
							? 'border-gray-400'
							: 'border-gray-900',
					)}
				>
					<span className="relative block h-10 w-10 flex-shrink-0 overflow-hidden rounded bg-gray-800">
						{thumbnails.length === 1 && (
							<div
								className="absolute inset-0 h-full w-full bg-cover bg-center"
								style={{ backgroundImage: `url(${thumbnails[0]})` }}
							/>
						)}
						{thumbnails.length === 2 && (
							<>
								<div
									className="absolute left-0 top-0 h-full w-1/2 bg-cover bg-center"
									style={{ backgroundImage: `url(${thumbnails[0]})` }}
								/>
								<div
									className="absolute right-0 top-0 h-full w-1/2 bg-cover bg-center"
									style={{ backgroundImage: `url(${thumbnails[1]})` }}
								/>
							</>
						)}
						{thumbnails.length === 3 && (
							<>
								<div
									className="absolute left-0 top-0 h-1/2 w-full bg-cover bg-center"
									style={{ backgroundImage: `url(${thumbnails[0]})` }}
								/>
								<div
									className="absolute bottom-0 left-0 h-1/2 w-1/2 bg-cover bg-center"
									style={{ backgroundImage: `url(${thumbnails[1]})` }}
								/>
								<div
									className="absolute bottom-0 right-0 h-1/2 w-1/2 bg-cover bg-center"
									style={{ backgroundImage: `url(${thumbnails[2]})` }}
								/>
							</>
						)}

						{thumbnails.length > 3 && (
							<>
								<div
									className="absolute left-0 top-0 h-1/2 w-1/2 bg-cover bg-center"
									style={{ backgroundImage: `url(${thumbnails[0]})` }}
								/>
								<div
									className="absolute right-0 top-0 h-1/2 w-1/2 bg-cover bg-center"
									style={{ backgroundImage: `url(${thumbnails[1]})` }}
								/>
								<div
									className="absolute bottom-0 left-0 h-1/2 w-1/2 bg-cover bg-center"
									style={{ backgroundImage: `url(${thumbnails[2]})` }}
								/>
								<div
									className="absolute bottom-0 right-0 h-1/2 w-1/2 bg-cover bg-center"
									style={{ backgroundImage: `url(${thumbnails[3]})` }}
								/>
							</>
						)}
					</span>
					<span className="flex min-w-0 flex-grow flex-col items-start">
						<span className="type-small truncate">All Saves</span>
						<span className="type-small truncate text-gray-500">All Saved Items</span>
					</span>
				</span>
			</SVLink>
		</li>
	);
};
const _SidebarBoardsList = ({
	type,
	openState,
	boards,
	isOwner,
	onNewBoardClick,
	lastThumbnails,
	team,
	teamHomePath,
}: Omit<Props, 'onToggleSidebar' | 'children'> & { teamHomePath: string }) => {
	return (
		<div
			className={cn(
				'duration-slide -lg:overflow-y-hidden -lg:overflow-x-auto -lg:w-full -lg:whitespace-nowrap -lg:mb-7 -lg:mt-2 -sm:my-0 h-full overflow-y-auto transition-all ease-in-out',
				openState === SidebarOpenState.OPEN
					? 'w-[calc(240px+12px)]'
					: openState === SidebarOpenState.MINIMAL
					? 'w-[calc(40px+12px)]'
					: 'w-[calc(-12px+12px)]',
			)}
		>
			<ul
				className={cn(
					'duration-slide -lg:flex-row -lg:space-y-0 -lg:space-x-[6px] -lg:w-auto -lg:min-w-full -lg:inline-flex flex flex-col space-y-[6px] overflow-x-hidden transition-all ease-in-out',
					openState === SidebarOpenState.OPEN
						? 'w-[calc(240px+12px)]'
						: openState === SidebarOpenState.MINIMAL
						? 'w-[calc(40px+12px)]'
						: 'w-[calc(-12px+12px)]',
					type === TYPES.TEAM && 'mt-2',
				)}
			>
				{type === TYPES.TEAM && lastThumbnails?.[0] && (
					<_AllSavedItems
						thumbnails={lastThumbnails}
						openState={openState}
						teamHomePath={teamHomePath}
					/>
				)}

				{boards.map((board) => (
					<_BoardItem
						key={board._id}
						board={board}
						openState={openState}
						type={type}
						team={team}
						teamHomePath={teamHomePath}
					/>
				))}
				{isOwner ? (
					<NewBoardItem key="new-board" onClick={onNewBoardClick} type={type} />
				) : null}
			</ul>
			<div className="-lg:h-2 h-16" />
		</div>
	);
};

const SVSidebarContent = React.memo(
	({
		type,
		openState,
		boards,
		isOwner,
		onNewBoardClick,
		onToggleSidebar,
		children,
		lastThumbnails,
		team,
	}: Props) => {
		const containerRef = useRef<HTMLDivElement>(null);
		const teamHomePath = useTeamHomePath();

		return (
			<div
				className={cn('-lg:flex-col flex w-full', type === TYPES.TEAM ? '-sm:mt-20' : '')}
				ref={containerRef}
			>
				<div className="relative flex flex-col">
					{onToggleSidebar && type !== TYPES.TEAM && (
						<div className="-lg:h-0 -lg:mb-0 mb-[7px] h-12 flex-shrink-0">
							<div
								className={cn(
									'duration-slide absolute transition-all ease-in-out',
									openState === SidebarOpenState.CLOSED
										? 'pl-[calc(var(--page-margin)-10px)]'
										: 'pl-[calc(var(--page-margin))]',
								)}
							>
								<_ToggleButton openState={openState} onToggle={onToggleSidebar} />
							</div>
						</div>
					)}
					<div
						className={cn(
							'duration-slide -lg:overflow-x-auto flex-shrink-0 flex-grow transition-all ease-in-out',
							openState === SidebarOpenState.OPEN
								? 'w-[calc(var(--page-margin)+240px+12px)]'
								: openState === SidebarOpenState.MINIMAL
								? 'w-[calc(var(--page-margin)+40px+12px)]'
								: 'w-0',
							type === TYPES.TEAM ? '-lg:flex -lg:w-full' : '-lg:hidden',
						)}
					>
						<div
							className={cn(
								'duration-slide -lg:flex -lg:w-full -lg:overflow-x-auto sticky left-0 top-0 h-full max-h-screen pl-[calc(var(--page-margin)-6px)] transition-all ease-in-out',
								openState === SidebarOpenState.OPEN
									? 'w-[calc(var(--page-margin)+240px+12px)]'
									: openState === SidebarOpenState.MINIMAL
									? 'w-[calc(var(--page-margin)+40px+12px)]'
									: 'w-0',
							)}
						>
							<div className="-lg:hidden">
								<SVTopNavSpacer containerRef={containerRef} />
							</div>
							<_SidebarBoardsList
								type={type}
								openState={openState}
								boards={boards}
								isOwner={isOwner}
								onNewBoardClick={onNewBoardClick}
								lastThumbnails={lastThumbnails}
								team={team ?? undefined}
								teamHomePath={teamHomePath}
							/>
						</div>
					</div>
				</div>
				<div className="min-w-0 flex-1">{children}</div>
			</div>
		);
	},
);

export const SVSidebarContentTYPES = TYPES;

export default SVSidebarContent;
